import { ArrowCounterClockwiseIcon, PencilSimpleIcon, TrashIcon } from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type JSX } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';

import { DeactivateSiteConfirmDialog } from '@/components/sites/DeactivateSiteConfirmDialog';
import { SiteKpiSummaryTab } from '@/components/sites/SiteKpiSummaryTab';
import { SiteLogsheetStatsTab } from '@/components/sites/SiteLogsheetStatsTab';
import { SiteMachinesTab } from '@/components/sites/SiteMachinesTab';
import { SiteUsersTab } from '@/components/sites/SiteUsersTab';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ApiError } from '@/lib/api/error';
import { useAuth } from '@/lib/auth/AuthContext';
import { toast } from '@/lib/toast';
import {
  deactivateSiteRequest,
  getSiteRequest,
  isDeactivateBlocked,
  reactivateSiteRequest,
} from '@/services/sites';
import type { DeactivateBlocked, SiteDetail } from '@/types/sites';

function formatDateTime(iso: string | null): string {
  if (iso === null) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export function SiteDetailPage(): JSX.Element {
  const { id: idParam } = useParams<{ id: string }>();
  const id = Number(idParam);
  const { permissions } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [deactivateOpen, setDeactivateOpen] = useState<boolean>(false);
  const [blocked, setBlocked] = useState<DeactivateBlocked | null>(null);
  const [deactivateError, setDeactivateError] = useState<string | null>(null);

  const detailQuery = useQuery<SiteDetail, ApiError>({
    queryKey: ['site', id],
    queryFn: () => getSiteRequest(id),
    enabled: Number.isFinite(id) && id > 0,
    staleTime: 5 * 60_000,
  });

  const deactivateMutation = useMutation<undefined, ApiError, { force: boolean }>({
    mutationFn: async ({ force }) => {
      await deactivateSiteRequest(id, force);
      return undefined;
    },
    onSuccess: () => {
      toast.success('Site deactivated');
      void queryClient.invalidateQueries({ queryKey: ['sites'] });
      void queryClient.invalidateQueries({ queryKey: ['site', id] });
      setDeactivateOpen(false);
      setBlocked(null);
      setDeactivateError(null);
      // Navigate back to the list — the deactivated row drops out of the
      // default `active` filter so leaving the user on /sites/:id (now showing
      // the inactive site detail with a Reactivate button) is confusing. The
      // E2E happy-path spec also waits on this URL transition (HI-01).
      void navigate('/sites');
    },
    onError: (err) => {
      if (isDeactivateBlocked(err)) {
        setBlocked(err.blocked);
        setDeactivateError(null);
      } else {
        setDeactivateError(err.detail ?? err.message);
      }
    },
  });

  const reactivateMutation = useMutation<undefined, ApiError>({
    mutationFn: async () => {
      await reactivateSiteRequest(id);
      return undefined;
    },
    onSuccess: () => {
      toast.success('Site reactivated');
      void queryClient.invalidateQueries({ queryKey: ['sites'] });
      void queryClient.invalidateQueries({ queryKey: ['site', id] });
    },
    onError: (err) => {
      toast.error('Could not reactivate', { description: err.detail ?? err.message });
    },
  });

  if (!permissions.includes('sites.read')) {
    return <Navigate to="/dashboard" replace />;
  }
  if (!Number.isFinite(id) || id <= 0) {
    return <Navigate to="/sites" replace />;
  }

  const canEdit = permissions.includes('sites.update');
  const canDeactivate = permissions.includes('sites.delete');
  const site = detailQuery.data;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {site?.name ?? 'Site'}{' '}
            {site && (
              <span
                className="ml-2 font-mono text-sm text-muted-foreground"
                data-testid="site-detail-code"
              >
                {site.code}
              </span>
            )}
          </h1>
          <p className="text-muted-foreground">
            {site?.location ?? 'Site profile, users, machines, and operational rollups.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {site && (
            <Badge
              variant={site.isActive ? 'default' : 'secondary'}
              data-testid="site-detail-status"
            >
              {site.isActive ? 'Active' : 'Inactive'}
            </Badge>
          )}
          {site !== undefined && canEdit && site.isActive && (
            // Inactive sites can't be edited via PUT (UpdateAsync filters them
            // out by IsActive); reactivation is the only state-change path
            // available. Gating the Edit button on site.isActive avoids a
            // confusing 404 toast (ME-02).
            <Button asChild data-testid="site-detail-edit">
              <Link to={`/sites/${String(id)}/edit`}>
                <PencilSimpleIcon size={16} weight="bold" /> Edit
              </Link>
            </Button>
          )}
          {site !== undefined && canDeactivate && site.isActive && (
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                setBlocked(null);
                setDeactivateError(null);
                setDeactivateOpen(true);
              }}
              data-testid="site-detail-deactivate"
            >
              <TrashIcon size={16} weight="bold" /> Deactivate
            </Button>
          )}
          {site !== undefined && canEdit && !site.isActive && (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                reactivateMutation.mutate();
              }}
              disabled={reactivateMutation.isPending}
              data-testid="site-detail-reactivate"
            >
              <ArrowCounterClockwiseIcon size={16} weight="bold" />{' '}
              {reactivateMutation.isPending ? 'Reactivating…' : 'Reactivate'}
            </Button>
          )}
        </div>
      </div>

      {detailQuery.isLoading && (
        <p className="text-sm text-muted-foreground" data-testid="site-detail-loading">
          Loading site…
        </p>
      )}
      {detailQuery.isError && (
        <p className="text-sm text-destructive" data-testid="site-detail-error">
          Failed to load site.
        </p>
      )}

      {site && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Profile</CardTitle>
              <CardDescription>Read-only view. Use Edit to make changes.</CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
              <div>
                <div className="text-muted-foreground text-xs">Code</div>
                <div className="font-mono">{site.code}</div>
              </div>
              <div>
                <div className="text-muted-foreground text-xs">Location</div>
                <div>{site.location}</div>
              </div>
              <div>
                <div className="text-muted-foreground text-xs">Site Head</div>
                <div data-testid="site-detail-sitehead">{site.siteHead?.fullName ?? '—'}</div>
              </div>
              <div>
                <div className="text-muted-foreground text-xs">Address</div>
                <div className="whitespace-pre-wrap">{site.address ?? '—'}</div>
              </div>
              <div className="sm:col-span-2">
                <div className="text-muted-foreground text-xs">Description</div>
                <div className="whitespace-pre-wrap">{site.description ?? '—'}</div>
              </div>
              <div>
                <div className="text-muted-foreground text-xs">Created</div>
                <div className="font-mono text-xs">{formatDateTime(site.createdAt)}</div>
              </div>
              <div>
                <div className="text-muted-foreground text-xs">Updated</div>
                <div className="font-mono text-xs">{formatDateTime(site.updatedAt)}</div>
              </div>
            </CardContent>
          </Card>

          <Tabs defaultValue="users" className="space-y-4">
            <TabsList data-testid="site-detail-tabs">
              <TabsTrigger value="machines" data-testid="site-detail-tab-machines">
                Machines
              </TabsTrigger>
              <TabsTrigger value="users" data-testid="site-detail-tab-users">
                Users
              </TabsTrigger>
              <TabsTrigger value="logsheets" data-testid="site-detail-tab-logsheets">
                Logsheet stats
              </TabsTrigger>
              <TabsTrigger value="kpis" data-testid="site-detail-tab-kpis">
                KPI summary
              </TabsTrigger>
            </TabsList>

            <TabsContent value="machines">
              <SiteMachinesTab siteId={id} />
            </TabsContent>
            <TabsContent value="users">
              <SiteUsersTab siteId={id} />
            </TabsContent>
            <TabsContent value="logsheets">
              <SiteLogsheetStatsTab siteId={id} />
            </TabsContent>
            <TabsContent value="kpis">
              <SiteKpiSummaryTab siteId={id} />
            </TabsContent>
          </Tabs>
        </>
      )}

      {site && (
        <DeactivateSiteConfirmDialog
          open={deactivateOpen}
          onOpenChange={(open) => {
            setDeactivateOpen(open);
            if (!open) {
              setBlocked(null);
              setDeactivateError(null);
              deactivateMutation.reset();
            }
          }}
          siteName={site.name}
          blocked={blocked}
          submitting={deactivateMutation.isPending}
          errorMessage={deactivateError}
          onConfirm={(force) => {
            setDeactivateError(null);
            deactivateMutation.mutate({ force });
          }}
          onCancel={() => {
            setDeactivateOpen(false);
            setBlocked(null);
            setDeactivateError(null);
          }}
        />
      )}
    </div>
  );
}
